import DownloadIcon from '@mui/icons-material/Download';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Link,
    Typography,
} from '@mui/material';
import { getAuthHeaders } from '../../utils/authUtils';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const formatDateTime = (value) => {
    if (!value) return 'N/A';
    try {
        return new Date(value).toLocaleString();
    } catch (e) {
        return String(value);
    }
};

const getStatusColor = (status) => {
    switch ((status || '').toUpperCase()) {
        case 'COMPLETED':
            return 'success';
        case 'PROCESSING':
            return 'info';
        case 'FAILED':
            return 'error';
        case 'NOT_STARTED':
            return 'default';
        default:
            return 'default';
    }
};

const downloadFile = async (audioFileId, type, filename, fallbackUrl) => {
    if (!audioFileId) return;
    const apiBaseUrl = process.env.REACT_APP_API_URL;
    const url = `${apiBaseUrl}/api/v2/audiofiles/${audioFileId}/download?type=${type}`;
    const headers = getAuthHeaders();
    try {
        // Use fetch instead of axios so the browser can stream large files
        // without buffering the entire response in JS heap memory.
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        // Delay revocation so the browser has time to start the download
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (err) {
        console.error('Download failed:', err);
        // Fallback: open directly in new tab
        if (fallbackUrl) window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
};

// Older failed records have no reason saved
const UNKNOWN_FAILURE = "The dubbing service couldn't process this audio. Try again, or contact support with the Dubbing Job ID.";

const formatAutoClearValue = (value) => {
    if (value === false) return 'Disabled';
    return 'Enabled (24h)';
};

const MetadataRow = ({ label, value }) => (
    <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={5} sm={4}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
        </Grid>
        <Grid item xs={7} sm={8}>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {value ?? 'N/A'}
            </Typography>
        </Grid>
    </Grid>
);

const AudioFileDetailsDialog = ({ open, onClose, audioFile }) => {
    useTrackUsage('AudioFileDetailsDialog', 'dialog', 'folder-browser', open);
    const status = audioFile?.dubbingStatus || 'NOT_STARTED';
    const sourceUrl = audioFile?.dubbingSourceUrl || '';
    const transformedUrl = audioFile?.dubbingTransformedUrl || '';
    const baseName = audioFile?.name || audioFile?.id || 'audio';
    const getExt = (url) => { const p = (url || '').split('?')[0]; return p.includes('.') ? p.split('.').pop() : 'wav'; };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Audio File Details</DialogTitle>
            <DialogContent>
                {!audioFile ? (
                    <Typography variant="body2" color="text.secondary">
                        No details available.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                                {audioFile.name || audioFile.id || 'Unnamed Audio'}
                            </Typography>
                            <Chip label={status} color={getStatusColor(status)} size="small" />
                            {status === 'FAILED' && (
                                <Alert severity="error" sx={{ mt: 1.5 }}>
                                    {audioFile.workStopReason || UNKNOWN_FAILURE}
                                </Alert>
                            )}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <MetadataRow label="Audio ID" value={audioFile.id} />
                        <MetadataRow label="Source Language" value={audioFile.sourceLanguage} />
                        <MetadataRow label="Voice Mixing Rate" value={audioFile.voiceMixingRate != null ? `${audioFile.voiceMixingRate}%` : 'N/A'} />
                        <MetadataRow label="Auto-clear in 24h" value={formatAutoClearValue(audioFile.clearDubbingAudioIn24Hours)} />
                        <MetadataRow label="Dubbing Job ID" value={audioFile.dubbingJobId} />
                        <MetadataRow label="Dubbing Started" value={formatDateTime(audioFile.dubbingStartedAt)} />
                        <MetadataRow label="Dubbing Completed" value={formatDateTime(audioFile.dubbingCompletedAt)} />
                        <MetadataRow label="Created By" value={audioFile.createdBy} />
                        <MetadataRow label="Created At" value={formatDateTime(audioFile.createdAt)} />
                        <MetadataRow label="Updated By" value={audioFile.updatedBy} />
                        <MetadataRow label="Updated At" value={formatDateTime(audioFile.updatedAt)} />

                        <Divider sx={{ my: 2 }} />

                        <MetadataRow
                            label="Original Audio URL"
                            value={
                                sourceUrl ? (
                                    <Link href={sourceUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                        Open Original Audio
                                    </Link>
                                ) : (
                                    'Not available'
                                )
                            }
                        />
                        <MetadataRow
                            label="Transformed Audio URL"
                            value={
                                transformedUrl ? (
                                    <Link href={transformedUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                        Open Transformed Audio
                                    </Link>
                                ) : (
                                    'Not available'
                                )
                            }
                        />
                        {audioFile.dubbingError && <MetadataRow label="Error details" value={audioFile.dubbingError} />}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={!sourceUrl}
                    onClick={() => downloadFile(audioFile?.id, 'original', `${baseName}_original.${getExt(sourceUrl)}`, sourceUrl)}
                >
                    Download Original
                </Button>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    disabled={!transformedUrl}
                    onClick={() => downloadFile(audioFile?.id, 'transformed', `${baseName}_transformed.${getExt(transformedUrl)}`, transformedUrl)}
                >
                    Download Transformed
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AudioFileDetailsDialog;
